/**
 * Pieces the Team sub-views of WP-45 share (plan docs/plans/lms-plan.md 13 tab
 * 6; WP-45). They live in the Matrix folder because the matrix is where most
 * of them start (a cell opens the person page, a waiver, a lift); People,
 * Sign-offs, External certificates, Reports and Verify import them from here.
 * The class strings are WP-43's (components/pickers/shared.tsx) and the design
 * skill's recipes (G13).
 *
 *   useAsync      one read with its own loading and error state, run again on
 *                 a site switch (every /api/lms call is site-scoped, 4.9), when
 *                 its key changes (a filter) and on reload(); an answer for a
 *                 superseded request is dropped
 *   LoadFailed    the failed read, loud, with Retry (G10: never a silent empty list)
 *   Loading       the skeleton rows while a read runs
 *   personHref    the person page's address (People, /lms/team/people/:userId)
 *   endOfLocalDayIso  a YYYY-MM-DD day as the instant its local day ends
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { outlineBtnCls } from '../../../../ui/styles';
import { apiErrorMessage } from '../../../api/client';
import { useLms } from '../../../LmsProvider';
import { lmsTab, lmsTabHref } from '../../config';

/** The Team tab's address (/lms/team; plan 18.3). */
export const TEAM_BASE = lmsTabHref(lmsTab('team'));

/** The People sub-view's address. */
export const PEOPLE_BASE = `${TEAM_BASE}/people`;

/** The person page of one person (People routes :userId under people/*). */
export function personHref(userId: string): string {
  return `${PEOPLE_BASE}/${encodeURIComponent(userId)}`;
}

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  setData: (update: (prev: T | null) => T | null) => void;
}

interface State<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

/**
 * One read (see the file header). `key` names everything the loader depends
 * on besides the site (a filter, an id); a new key runs the read again. While
 * a new read runs the previous data stays on screen (loading is true), so a
 * filter change does not blank a table.
 */
export function useAsync<T>(loader: () => Promise<T>, key: string, errorText: string, enabled = true): AsyncState<T> {
  const { siteId } = useLms();
  const [state, setState] = useState<State<T>>({ data: null, loading: enabled, error: null });
  const [tick, setTick] = useState(0);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    if (!enabled) {
      setState({ data: null, loading: false, error: null });
      return undefined;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    loaderRef.current().then(
      (data) => {
        if (!cancelled) setState({ data, loading: false, error: null });
      },
      (err: unknown) => {
        if (!cancelled) setState({ data: null, loading: false, error: apiErrorMessage(err, errorText) });
      }
    );
    return () => {
      cancelled = true;
    };
  }, [siteId, tick, key, errorText, enabled]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  const setData = useCallback((update: (prev: T | null) => T | null) => {
    setState((s) => ({ ...s, data: update(s.data) }));
  }, []);

  return { data: state.data, loading: state.loading, error: state.error, reload, setData };
}

/** A failed read with Retry (G10). Announced politely: a block that could not load is not an emergency. */
export function LoadFailed({ message, onRetry, testId }: { message: string; onRetry: () => void; testId?: string }) {
  return (
    <div
      role="status"
      data-testid={testId}
      className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400"
    >
      <span>{message}</span>
      <button type="button" className={outlineBtnCls()} onClick={onRetry}>
        <RefreshCw className="h-4 w-4" aria-hidden="true" />
        Retry
      </button>
    </div>
  );
}

/** Skeleton rows while a read runs (the design skill's skeleton recipe). */
export function Loading({ rows = 3, testId }: { rows?: number; testId?: string }) {
  return (
    <div className="space-y-3 py-2" aria-busy="true" data-testid={testId}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="space-y-1.5">
          <div className="h-4 w-2/3 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-3 w-1/3 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
        </div>
      ))}
    </div>
  );
}

/** A YYYY-MM-DD day as the ISO instant its local day ends (a due date picked as a day, 8.4). */
export function endOfLocalDayIso(day: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59, 0);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** A person's name with a "Left" word when the account is soft-deleted (18.21: leavers stay listed, flagged). */
export function LeaverTag({ deleted }: { deleted: boolean | null | undefined }) {
  if (!deleted) return null;
  return (
    <span className="ml-1.5 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700/60 dark:text-gray-300">
      Left
    </span>
  );
}
